import axios from 'axios';

const runtimeOrigin = typeof window !== 'undefined' ? window.location.origin : '';
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (runtimeOrigin ? `${runtimeOrigin}/api` : 'http://localhost:8080/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor per aggiungere token alle richieste
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    
    // Aggiungi header di impersonazione se presente
    const impersonatedUserId =
      localStorage.getItem('impersonated_user_id') ||
      localStorage.getItem('impersonated_user_id_backup');
    if (impersonatedUserId) {
      config.headers['X-Impersonate-User-ID'] = impersonatedUserId;
    }
  }
  return config;
});

// Interceptor per gestire errori di autenticazione
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Non facciamo redirect automatico per evitare loop
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (username: string, password: string) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  
  getProfile: async (token: string) => {
    // L'interceptor aggiunge già Authorization header, non serve passarlo qui
    // Se c'è impersonazione attiva, il middleware imposterà user nel contesto
    // e GetProfile restituirà l'utente impersonato
    const response = await api.get('/auth/profile');
    return response.data;
  },
  
  requestPasswordReset: async (email: string) => {
    const response = await api.post('/auth/password/reset', { email });
    return response.data;
  },
  
  confirmPasswordReset: async (token: string, password: string) => {
    const response = await api.post('/auth/password/confirm', { token, password });
    return response.data;
  },
  
  changePassword: async (oldPassword: string, newPassword: string) => {
    const response = await api.post('/auth/password/change', { old_password: oldPassword, new_password: newPassword });
    return response.data;
  },
  
  forcePasswordChange: async (newPassword: string) => {
    const response = await api.post('/auth/password/force-change', { new_password: newPassword });
    return response.data;
  },
};

export const presenceAPI = {
  checkIn: async (notes: string) => {
    const response = await api.post('/presence/checkin', { notes });
    return response.data;
  },
  
  checkOut: async (notes: string) => {
    const response = await api.post('/presence/checkout', { notes });
    return response.data;
  },
  
  getStatus: async () => {
    const response = await api.get('/presence/status');
    return response.data;
  },
  
  getHistory: async (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    
    const response = await api.get(`/presence/history?${params.toString()}`);
    return response.data;
  },

  getActivePresences: async () => {
    const response = await api.get('/presence/active');
    return response.data;
  },
};

// API per gestione checkout dimenticati (admin)
export const checkoutAdminAPI = {
  createPresence: async (payload: {
    user_id: number;
    check_in: string;
    check_out?: string | null;
    work_hours?: number | null;
    overtime_hours?: number | null;
    admin_note?: string | null;
    notes?: string | null;
  }) => {
    const response = await api.post('/admin/presence', payload);
    return response.data;
  },
  // Lista checkout pendenti
  getPendingCheckouts: async () => {
    const response = await api.get('/admin/presence/pending-checkouts');
    return response.data;
  },

  updatePresence: async (presenceId: number, payload: {
    check_in?: string | null;
    check_out?: string | null;
    work_hours?: number | null;
    overtime_hours?: number | null;
    notes?: string | null;
    admin_note?: string | null;
  }) => {
    const response = await api.put(`/admin/presence/${presenceId}`, payload);
    return response.data;
  },

  // Storico presenze per utente specifico (admin)
  getUserHistory: async (userId: number, from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const query = params.toString();
    const url = `/admin/presence/history/${userId}${query ? `?${query}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },

  // Forza checkout per una presenza
  forceCheckout: async (presenceId: number, checkOutTime?: string, notes?: string) => {
    const response = await api.post('/admin/presence/force-checkout', {
      presence_id: presenceId,
      check_out_time: checkOutTime,
      notes: notes || '',
    });
    return response.data;
  },

  // Checkout automatico per presenze scadute
  autoCheckoutExpired: async (maxHours: number = 12) => {
    const response = await api.post(`/admin/presence/auto-checkout-expired?max_hours=${maxHours}`);
    return response.data;
  },
};

export const reportAPI = {
  getDailyReport: async (date?: string) => {
    const params = date ? `?date=${date}` : '';
    const response = await api.get(`/reports/daily${params}`);
    return response.data;
  },
  
  getWeeklyReport: async (week?: string) => {
    const params = week ? `?week=${week}` : '';
    const response = await api.get(`/reports/weekly${params}`);
    return response.data;
  },
  
  getMonthlyReport: async (month?: string) => {
    const params = month ? `?month=${month}` : '';
    const response = await api.get(`/reports/monthly${params}`);
    return response.data;
  },
  
  getDashboard: async () => {
    const response = await api.get('/admin/dashboard');
    return response.data;
  },

  getRegularIrregularReport: async (date?: string) => {
    const params = date ? `?date=${date}` : '';
    const response = await api.get(`/admin/reports/regular-irregular${params}`);
    return response.data;
  },
};

export const employeeAPI = {
  // Recupera il proprio profilo dipendente
  getMyEmployee: async () => {
    const response = await api.get('/employees/me');
    return response.data;
  },

  // Ricerca dipendenti
  searchEmployees: async (filters: any) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value.toString());
    });
    const response = await api.get(`/admin/employees/search?${params.toString()}`);
    return response.data;
  },

  // Recupera dipendente per ID
  getEmployee: async (id: number) => {
    const response = await api.get(`/admin/employees/${id}`);
    return response.data;
  },

  // Recupera dipendente per codice fiscale
  getEmployeeByCF: async (codiceFiscale: string) => {
    const response = await api.get(`/admin/employees/cf/${codiceFiscale}`);
    return response.data;
  },

  // Crea nuovo dipendente
  createEmployee: async (employeeData: any) => {
    const response = await api.post('/admin/employees', employeeData);
    return response.data;
  },

  // Aggiorna dipendente
  updateEmployee: async (id: number, employeeData: any) => {
    const response = await api.put(`/admin/employees/${id}`, employeeData);
    return response.data;
  },

  // Crea utente per dipendente esistente
  createUserForEmployee: async (id: number, userData: { username: string; password: string; role: string; is_super_admin?: boolean }) => {
    const response = await api.post(`/admin/employees/${id}/create-user`, userData);
    return response.data;
  },

  updateExpectedHours: async (employeeId: number, payload: {
    daily_hours?: number;
    weekly_hours?: number;
    weekly_days?: number;
    admin_note?: string;
  }) => {
    const response = await api.put(`/admin/employees/${employeeId}/expected-hours`, payload);
    return response.data;
  },

  // Elimina dipendente
  deleteEmployee: async (id: number) => {
    const response = await api.delete(`/admin/employees/${id}`);
    return response.data;
  },

  // Recupera dipendenti per dipartimento
  getEmployeesByDepartment: async (department: string) => {
    const response = await api.get(`/admin/employees/department/${department}`);
    return response.data;
  },

  // Recupera lista dipartimenti
  getDepartments: async () => {
    const response = await api.get('/admin/employees/departments');
    return response.data;
  },

  // Recupera lista enti distinti
  getEnti: async () => {
    const response = await api.get('/admin/employees/enti');
    // L'API restituisce {enti: [...]}, quindi restituiamo direttamente l'array
    return response.data.enti || [];
  },

  getRegioni: async () => {
    const response = await api.get('/admin/employees/regioni');
    return response.data;
  },

  getTipiSede: async () => {
    const response = await api.get('/admin/employees/tipi-sede');
    return response.data;
  },

  getRuoli: async () => {
    const response = await api.get('/admin/employees/ruoli');
    return response.data;
  },

  // Recupera dipendenti eliminati
  getDeletedEmployees: async (filters: any) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value.toString());
    });
    const response = await api.get(`/admin/employees/deleted?${params.toString()}`);
    return response.data;
  },

  // Ripristina dipendente eliminato
  restoreEmployee: async (id: number) => {
    const response = await api.post(`/admin/employees/${id}/restore`);
    return response.data;
  },

  // Elimina definitivamente dipendente
  permanentDeleteEmployee: async (id: number) => {
    const response = await api.delete(`/admin/employees/${id}/permanent`);
    return response.data;
  },

  // Import Excel
  // Scarica report importazione
  downloadImportReport: async (importResult: any) => {
    const response = await api.post('/admin/employees/import/report', importResult, {
      responseType: 'blob',
    });
    
    // Crea un blob e scarica il file
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Estrai il nome del file dall'header Content-Disposition se presente
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'report_importazione.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  importFromExcel: async (file: File, onProgress?: (progress: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Simula progresso durante elaborazione
    const simulateProgress = () => {
      let progress = 0;
      const interval = setInterval(() => {
        if (onProgress && progress < 90) {
          progress += 10;
          onProgress(progress);
        }
      }, 500);
      return interval;
    };
    
    const interval = simulateProgress();
    
    try {
      const response = await api.post('/admin/employees/import/excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(Math.min(percentCompleted, 10)); // Upload max 10%
          }
        },
      });
      
      if (onProgress) {
        onProgress(100);
      }
      clearInterval(interval);
      return response.data;
    } catch (error) {
      clearInterval(interval);
      throw error;
    }
  },
};

// API per verificare gli utenti (debug)
export const debugAPI = {
  getUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  getAllUsersIncludingInactive: async () => {
    const response = await api.get('/admin/users/all');
    return response.data;
  },

  enableUsers: async (userIDs: number[]) => {
    const response = await api.post('/admin/users/enable', { user_ids: userIDs });
    return response.data;
  },

  disableUsers: async (userIDs: number[]) => {
    const response = await api.post('/admin/users/disable', { user_ids: userIDs });
    return response.data;
  },

  sendTempCredentials: async (userIDs: number[]) => {
    const response = await api.post('/admin/users/send-credentials', { user_ids: userIDs });
    return response.data;
  },

  updateUser: async (userId: number, updates: { username?: string; email?: string; active?: boolean }) => {
    const response = await api.put(`/admin/users/${userId}`, updates);
    return response.data;
  },

  createUser: async (userData: {
    username: string;
    email: string;
    password: string;
    codice_fiscale: string;
    nome: string;
    cognome: string;
    ore_lavoro?: number;
    giorni_lavoro?: number;
    data_apertura_posizione?: string;
    data_chiusura_posizione?: string;
    role: string;
    department?: string;
    manager_id?: number;
    is_super_admin?: boolean;
    active?: boolean;
  }) => {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },

  getEmployees: async () => {
    const response = await api.get('/employees/search');
    return response.data;
  },
};

// API per monitoraggio anomalie
export const anomaliesAPI = {
  // Statistiche dashboard
  getDashboardStats: async () => {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  },

  // Anomalie giornaliere
  getDailyAnomalies: async (date?: string) => {
    const params = date ? `?date=${date}` : '';
    const response = await api.get(`/admin/anomalies/daily${params}`);
    return response.data;
  },

  // Anomalie settimanali
  getWeeklyAnomalies: async (week?: string) => {
    const params = week ? `?week=${week}` : '';
    const response = await api.get(`/admin/anomalies/weekly${params}`);
    return response.data;
  },

  // Anomalie mensili
  getMonthlyAnomalies: async (month?: string) => {
    const params = month ? `?month=${month}` : '';
    const response = await api.get(`/admin/anomalies/monthly${params}`);
    return response.data;
  },

  // Elenchi dipendenti per categoria
  getEmployeesAlLavoro: async () => {
    const response = await api.get('/admin/dashboard/employees/al-lavoro');
    return response.data;
  },

  getEmployeesContrattoCessato: async () => {
    const response = await api.get('/admin/dashboard/employees/contratto-cessato');
    return response.data;
  },

  getEmployeesContrattoInScadenza: async () => {
    const response = await api.get('/admin/dashboard/employees/contratto-in-scadenza');
    return response.data;
  },

  getEmployeesInPermesso: async () => {
    const response = await api.get('/admin/dashboard/employees/in-permesso');
    return response.data;
  },

  getEmployeesInFerie: async () => {
    const response = await api.get('/admin/dashboard/employees/in-ferie');
    return response.data;
  },
};

export const groupDashboardAPI = {
  getSummary: async () => {
    const response = await api.get('/admin/group-dashboard/summary');
    return response.data;
  },
};

// API per gestione ferie/permessi/malattia
export const leaveAPI = {
  // Crea nuova richiesta
  createLeaveRequest: async (requestData: {
    request_type: 'ferie' | 'permesso' | 'malattia' | 'altri';
    start_date: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
    duration_hours?: number;
    notes?: string;
  }) => {
    const response = await api.post('/leave/request', requestData);
    return response.data;
  },

  // Le mie richieste
  getMyRequests: async () => {
    const response = await api.get('/leave/my-requests');
    return response.data;
  },

  // Saldo ferie/permessi
  getBalance: async (year?: number) => {
    const params = year ? `?year=${year}` : '';
    const response = await api.get(`/leave/balance${params}`);
    return response.data;
  },

  // Calendario assenze
  getCalendar: async (month?: string) => {
    const params = month ? `?month=${month}` : '';
    const response = await api.get(`/leave/calendar${params}`);
    return response.data;
  },

  // Saldo dettagliato per dipendente
  getDetailedBalance: async (year?: number) => {
    const params = year ? `?year=${year}` : '';
    const response = await api.get(`/leave/balance/detailed${params}`);
    return response.data;
  },
};

// API per approvazione ferie (admin)
export const leaveAdminAPI = {
  // Richieste in attesa
  getPendingRequests: async () => {
    const response = await api.get('/admin/leave/pending');
    return response.data;
  },

  // Approva/rifiuta richiesta
  approveRequest: async (requestId: number, data: {
    status: 'approved' | 'rejected';
    approver_notes?: string;
  }) => {
    const response = await api.put(`/admin/leave/${requestId}/approve`, data);
    return response.data;
  },

  // Crea permesso direttamente per un dipendente (approvato automaticamente)
  createLeaveForEmployee: async (employeeId: number, requestData: {
    request_type: 'ferie' | 'permesso' | 'malattia' | 'altri';
    start_date: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
    duration_hours?: number;
    notes?: string;
    leave_reason?: string;
  }) => {
    const response = await api.post(`/admin/leave/employee/${employeeId}`, requestData);
    return response.data;
  },

  // Storico completo richieste ferie/permessi
  getLeaveHistory: async (params: {
    date_from?: string;
    date_to?: string;
    request_type?: string;
    status?: string;
    group_id?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.date_from) searchParams.set('date_from', params.date_from);
    if (params.date_to) searchParams.set('date_to', params.date_to);
    if (params.request_type) searchParams.set('request_type', params.request_type);
    if (params.status) searchParams.set('status', params.status);
    if (params.group_id) searchParams.set('group_id', params.group_id.toString());
    const response = await api.get(`/admin/leave/history?${searchParams.toString()}`);
    return response.data;
  },

  // Elimina permesso/ferie
  deleteLeave: async (requestId: number) => {
    const response = await api.delete(`/admin/leave/${requestId}`);
    return response.data;
  },

  // Import saldi ferie da Excel
  importBalances: async (formData: FormData) => {
    const response = await api.post('/admin/leave/import/excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.result;
  },

  // Overview saldi tutti i dipendenti
  getBalancesOverview: async (year?: number, ente?: string) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (ente) params.append('ente', ente);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/admin/leave/balances/overview${queryString}`);
    return response.data;
  },
};

// API per gestione gruppi admin (solo super admin)
export const adminGroupsAPI = {
  // Lista tutti i gruppi
  getAllGroups: async () => {
    const response = await api.get('/admin/groups');
    return response.data;
  },

  // Crea nuovo gruppo
  createGroup: async (data: { name: string; description?: string; admin_manages_timesheets?: boolean; timesheet_export_policy?: 'inherit' | 'allow' | 'deny' }) => {
    const response = await api.post('/admin/groups', data);
    return response.data;
  },

  // Ottieni gruppo per ID
  getGroupById: async (groupId: number) => {
    const response = await api.get(`/admin/groups/${groupId}`);
    return response.data;
  },

  // Aggiorna gruppo
  updateGroup: async (groupId: number, data: { name: string; description?: string; admin_manages_timesheets?: boolean; timesheet_export_policy?: 'inherit' | 'allow' | 'deny' }) => {
    const response = await api.put(`/admin/groups/${groupId}`, data);
    return response.data;
  },

  getTimesheetExportPolicy: async () => {
    const response = await api.get('/timesheet/export-policy');
    return response.data;
  },

  getGlobalTimesheetExportPolicy: async () => {
    const response = await api.get('/admin/groups/timesheet-export-policy/global');
    return response.data;
  },

  setGlobalTimesheetExportPolicy: async (enabled: boolean) => {
    const response = await api.put('/admin/groups/timesheet-export-policy/global', { enabled });
    return response.data;
  },

  // Elimina gruppo
  deleteGroup: async (groupId: number) => {
    const response = await api.delete(`/admin/groups/${groupId}`);
    return response.data;
  },

  // Ottieni membri (admin) di un gruppo
  getGroupMembers: async (groupId: number) => {
    const response = await api.get(`/admin/groups/${groupId}/members`);
    return response.data;
  },

  // Aggiungi admin a gruppo
  addAdminToGroup: async (groupId: number, adminUserId: number) => {
    const response = await api.post(`/admin/groups/${groupId}/members`, { admin_user_id: adminUserId });
    return response.data;
  },

  // Rimuovi admin da gruppo
  removeAdminFromGroup: async (groupId: number, userId: number) => {
    const response = await api.delete(`/admin/groups/${groupId}/members/${userId}`);
    return response.data;
  },

  // Ottieni dipendenti assegnati a un gruppo
  getGroupEmployees: async (groupId: number) => {
    const response = await api.get(`/admin/groups/${groupId}/employees`);
    return response.data;
  },

  // API per admin normali (i propri gruppi)
  getMyGroups: async () => {
    const response = await api.get('/admin/my-groups');
    return response.data;
  },

  getMyGroupById: async (groupId: number) => {
    const response = await api.get(`/admin/my-groups/${groupId}`);
    return response.data;
  },

  getMyGroupEmployees: async (groupId: number) => {
    const response = await api.get(`/admin/my-groups/${groupId}/employees`);
    return response.data;
  },

  // Aggiungi dipendente al proprio gruppo (per admin normali)
  addEmployeeToMyGroup: async (groupId: number, employeeId: number) => {
    const response = await api.post(`/admin/my-groups/${groupId}/employees`, { employee_id: employeeId });
    return response.data;
  },

  // Rimuovi dipendente dal proprio gruppo (per admin normali)
  removeEmployeeFromMyGroup: async (groupId: number, employeeId: number) => {
    const response = await api.delete(`/admin/my-groups/${groupId}/employees/${employeeId}`);
    return response.data;
  },

  // Aggiungi dipendente a gruppo (solo super admin)
  addEmployeeToGroup: async (groupId: number, employeeId: number) => {
    const response = await api.post(`/admin/groups/${groupId}/employees`, { employee_id: employeeId });
    return response.data;
  },

  // Rimuovi dipendente da gruppo (solo super admin)
  removeEmployeeFromGroup: async (groupId: number, employeeId: number) => {
    const response = await api.delete(`/admin/groups/${groupId}/employees/${employeeId}`);
    return response.data;
  },

  // Ottieni dipendenti senza gruppo (solo super admin)
  getEmployeesWithoutGroup: async () => {
    const response = await api.get('/admin/groups/employees/without-group');
    return response.data;
  },

  // Verifica se l'utente corrente è membro di un gruppo admin
  isUserGroupAdmin: async () => {
    const response = await api.get('/admin/is-group-admin');
    return response.data;
  },

  // Verifica se il dipendente dell'utente corrente appartiene a un gruppo con gestione cartellini admin
  isEmployeeInAdminManagedGroup: async () => {
    const response = await api.get('/employee/is-in-admin-managed-group');
    return response.data;
  },
};

// API per validazione cartellini
export const validationAPI = {
  // Preview validazione (tutti gli utenti)
  getValidationPreview: async (year: number, month: number, userId?: number) => {
    const params = new URLSearchParams();
    params.append('year', year.toString());
    params.append('month', month.toString());
    if (userId) {
      params.append('user_id', userId.toString());
    }
    const response = await api.get(`/presence/validation/preview?${params.toString()}`);
    return response.data;
  },

  // Valida cartellino
  validatePresence: async (data: {
    user_id?: number;
    year: number;
    month: number;
    validation_notes?: string;
  }) => {
    const response = await api.post('/presence/validation/validate', data);
    return response.data;
  },

  // Le mie validazioni
  getMyValidations: async (year?: number, month?: number) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    const response = await api.get(`/presence/validation/my-validations?${params.toString()}`);
    return response.data;
  },

  // Lista validazioni per admin (con filtri)
  getValidationsForAdmin: async (filters?: {
    year?: number;
    month?: number;
    has_anomalies?: boolean;
    group_id?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.year) params.append('year', filters.year.toString());
    if (filters?.month) params.append('month', filters.month.toString());
    if (filters?.has_anomalies !== undefined) {
      params.append('has_anomalies', filters.has_anomalies.toString());
    }
    if (filters?.group_id) {
      params.append('group_id', filters.group_id.toString());
    }
    const queryString = params.toString();
    const url = `/admin/presence/validations${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },

  // Validazioni per un dipendente specifico
  getEmployeeValidations: async (employeeId: number, year?: number, month?: number) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    const queryString = params.toString();
    const url = `/admin/presence/validations/employee/${employeeId}${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },

  // Log accessi per una validazione
  getValidationAccessLog: async (validationId: number) => {
    const response = await api.get(`/admin/presence/validations/${validationId}/access-log`);
    return response.data;
  },

  // Esportazione massiva cartellini regolari
  getRegularValidationsForExport: async (year: number, month: number, includeProvisional?: boolean) => {
    const params = new URLSearchParams();
    params.append('year', year.toString());
    params.append('month', month.toString());
    if (includeProvisional) {
      params.append('include_provisional', 'true');
    }
    const response = await api.get(`/admin/presence/validations/export/regular?${params.toString()}`);
    return response.data;
  },

  // Dipendenti in attesa di validazione
  getPendingValidations: async (year: number, month: number) => {
    const params = new URLSearchParams();
    params.append('year', year.toString());
    params.append('month', month.toString());
    const response = await api.get(`/admin/presence/validations/pending?${params.toString()}`);
    return response.data;
  },

  // Validazione batch
  batchValidate: async (data: {
    employee_ids: number[];
    year: number;
    month: number;
    validation_notes?: string;
  }) => {
    const response = await api.post('/admin/presence/validations/batch-validate', data);
    return response.data;
  },
};

// API per inserimento presenze guidato (admin gruppo)
export const guidedPresenceAPI = {
  getManagedGroups: async () => {
    const response = await api.get('/admin/guided-presence/groups');
    return response.data;
  },

  getDailyPresences: async (date: string, groupId?: number) => {
    let url = `/admin/guided-presence/daily?date=${date}`;
    if (groupId) url += `&group_id=${groupId}`;
    const response = await api.get(url);
    return response.data;
  },

  getWeeklyPresences: async (weekStart: string, groupId?: number) => {
    let url = `/admin/guided-presence/weekly?week_start=${weekStart}`;
    if (groupId) url += `&group_id=${groupId}`;
    const response = await api.get(url);
    return response.data;
  },

  getMonthlySummary: async (year: number, month: number, groupId?: number) => {
    let url = `/admin/guided-presence/monthly-summary?year=${year}&month=${month}`;
    if (groupId) url += `&group_id=${groupId}`;
    const response = await api.get(url);
    return response.data;
  },

  getMonthlyAbsenceCalendar: async (year: number, month: number, groupId?: number) => {
    let url = `/admin/guided-presence/absence-calendar?year=${year}&month=${month}`;
    if (groupId) url += `&group_id=${groupId}`;
    const response = await api.get(url);
    return response.data;
  },

  bulkSave: async (entries: Array<{
    employee_id: number;
    user_id: number;
    date: string;
    check_in?: string | null;
    check_out?: string | null;
    admin_note?: string;
  }>) => {
    const BATCH_SIZE = 400;

    if (entries.length <= BATCH_SIZE) {
      const response = await api.post('/admin/guided-presence/bulk-save', { entries });
      return response.data;
    }

    // Invio in batch da 400
    const result = { total_processed: 0, total_created: 0, total_updated: 0, total_skipped: 0, total_failed: 0, results: [] as any[] };
    for (let i = 0; i < entries.length; i += BATCH_SIZE) {
      const chunk = entries.slice(i, i + BATCH_SIZE);
      const response = await api.post('/admin/guided-presence/bulk-save', { entries: chunk });
      const data = response.data;
      result.total_processed += data.total_processed || 0;
      result.total_created += data.total_created || 0;
      result.total_updated += data.total_updated || 0;
      result.total_skipped += data.total_skipped || 0;
      result.total_failed += data.total_failed || 0;
      if (data.results) result.results.push(...data.results);
    }
    return result;
  },
};

// API per impersonazione (solo super admin)
export const impersonationAPI = {
  // Inizia impersonazione
  startImpersonation: async (targetUserId: number) => {
    // NON inviare l'header X-Impersonate-User-ID qui perché la sessione non esiste ancora
    // L'header verrà aggiunto automaticamente dall'interceptor per le richieste successive
    const response = await api.post('/admin/impersonation/start', {
      target_user_id: targetUserId,
    });
    return response.data;
  },

  // Termina impersonazione
  endImpersonation: async () => {
    const response = await api.post('/admin/impersonation/end');
    return response.data;
  },

  // Recupera contesto impersonazione corrente
  getImpersonationContext: async () => {
    const response = await api.get('/admin/impersonation/context');
    return response.data;
  },
};
